import React, { useContext, useCallback } from 'react';
import Head from 'next/head';
import { Link } from '@scentregroup/shared/link';
import { Box } from '@hub/box';
import { CoreContainer } from '@hub/core-container';
import { H1 } from '@hub/heading';
import { AdTargetingContext, BasicPageContext, CentreContext } from '@scentregroup/shared/context';
import { UniversalFooter } from '../../components/universal-footer';
import { completelyCanonicalUrl } from '../../helpers/canonical-link';
import trackClickEvents, { categories, labels } from '../analytics/trackEvents';
import { Header, HeaderBreadcrumb, HeaderInner, HeaderMenus, HeaderNavigationBar, HeaderOuter, HeaderPrimaryMenu, HeaderQuickLinks, HeaderSearch, HeaderSecondaryMenu } from '../header';
import { useLocalUser } from '../../hooks/use-local-user';
import registerRedirect from '../../helpers/register-redirect';
import { MagicLinkValue, useMagicLink } from '../magic-link';
import { useLayoutSearch } from './use-layout-search';
import { useLayoutLogo } from './use-layout-logo';
import { useLayoutNavigation } from './use-layout-navigation';
import { useLayoutAnalytics } from './use-layout-analytics';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
function safeMeta(value) {
  return typeof value === 'string' && value === '' ? undefined : value;
}
const handleClickTopStrip = () => {
  trackClickEvents.clicked(categories.UNIVERSAL_HEADER, labels.BENEFITS_STRIP);
};
export default function Layout(_ref) {
  var _metaData$twitter, _safeMeta, _metaData$openGraph, _safeMeta2, _metaData$openGraph2, _safeMeta3, _metaData$openGraph3, _metaData$openGraph4, _metaData$twitter2, _header$data, _header$data2, _header$data3, _ref2, _user$firstName;
  let {
    country,
    centre,
    header,
    metaData,
    pageTitle,
    showFooter = true,
    taskMode = false,
    backLink,
    children,
    mainStyles,
    topStrip: topStripProps,
    showRobotsMetaTag = true,
    quickLinks,
    breadcrumbs,
    showCVPStrip = true,
    onClickBack
  } = _ref;
  const showChrome = !useContext(BasicPageContext);
  const canonical = safeMeta(metaData === null || metaData === void 0 ? void 0 : metaData.canonical);
  const title = safeMeta(metaData === null || metaData === void 0 ? void 0 : metaData.title);
  const description = safeMeta(metaData === null || metaData === void 0 ? void 0 : metaData.description);
  const twitterCreator = safeMeta(metaData === null || metaData === void 0 || (_metaData$twitter = metaData.twitter) === null || _metaData$twitter === void 0 ? void 0 : _metaData$twitter.creator);
  const openGraphUrl = (_safeMeta = safeMeta(metaData === null || metaData === void 0 || (_metaData$openGraph = metaData.openGraph) === null || _metaData$openGraph === void 0 ? void 0 : _metaData$openGraph.url)) !== null && _safeMeta !== void 0 ? _safeMeta : canonical;
  const openGraphTitle = (_safeMeta2 = safeMeta(metaData === null || metaData === void 0 || (_metaData$openGraph2 = metaData.openGraph) === null || _metaData$openGraph2 === void 0 ? void 0 : _metaData$openGraph2.title)) !== null && _safeMeta2 !== void 0 ? _safeMeta2 : title;
  const openGraphDescription = (_safeMeta3 = safeMeta(metaData === null || metaData === void 0 || (_metaData$openGraph3 = metaData.openGraph) === null || _metaData$openGraph3 === void 0 ? void 0 : _metaData$openGraph3.description)) !== null && _safeMeta3 !== void 0 ? _safeMeta3 : description;
  const openGraphImage = safeMeta(metaData === null || metaData === void 0 || (_metaData$openGraph4 = metaData.openGraph) === null || _metaData$openGraph4 === void 0 ? void 0 : _metaData$openGraph4.image);
  let twitterCard = safeMeta(metaData === null || metaData === void 0 || (_metaData$twitter2 = metaData.twitter) === null || _metaData$twitter2 === void 0 ? void 0 : _metaData$twitter2.card);
  if (openGraphImage) {
    var _twitterCard;
    (_twitterCard = twitterCard) !== null && _twitterCard !== void 0 ? _twitterCard : twitterCard = 'summary_large_image';
  } else if (openGraphTitle || openGraphDescription) {
    var _twitterCard2;
    (_twitterCard2 = twitterCard) !== null && _twitterCard2 !== void 0 ? _twitterCard2 : twitterCard = 'summary';
  }

  // Slightly different from the logic in Westfield Direct layout as it has to deal with legacy canonical support
  const completeCanonical = canonical && completelyCanonicalUrl(canonical, metaData === null || metaData === void 0 ? void 0 : metaData.canonicalParams);
  const {
    logoLink,
    logoLabel,
    logoSlug
  } = useLayoutLogo(centre, header === null || header === void 0 ? void 0 : header.variant);
  const {
    isOpenPrimaryMenu,
    setIsOpenPrimaryMenu,
    isOpenSecondaryMenu,
    setIsOpenSecondaryMenu,
    giftCards,
    primaryMenuItems,
    secondaryMenuItems
  } = useLayoutNavigation(header === null || header === void 0 || (_header$data = header.data) === null || _header$data === void 0 ? void 0 : _header$data.giftCards, header === null || header === void 0 || (_header$data2 = header.data) === null || _header$data2 === void 0 ? void 0 : _header$data2.primaryLinks, header === null || header === void 0 || (_header$data3 = header.data) === null || _header$data3 === void 0 ? void 0 : _header$data3.tertiaryLinks);
  const [isOpenSearchModal, setIsOpenSearchModal, specialSearchKind, getSources, searchOnSubmit, reshape] = useLayoutSearch(country, centre);
  const user = useLocalUser();
  const [magicLink, setMagicLink] = useMagicLink();
  React.useEffect(() => {
    if (magicLink === MagicLinkValue.stores || magicLink === MagicLinkValue.centres) {
      setIsOpenSearchModal(true);
    }
  }, [magicLink, setIsOpenSearchModal]);
  React.useEffect(() => {
    if (!isOpenSearchModal) {
      setMagicLink();
    }
  }, [isOpenSearchModal, setMagicLink]);
  const {
    centres
  } = (header === null || header === void 0 ? void 0 : header.data) || {};
  const handleClosePrimaryMenu = React.useCallback(() => setIsOpenPrimaryMenu(false), [setIsOpenPrimaryMenu]);
  const analyticsHandlers = useLayoutAnalytics();
  const onSubmit = useCallback(query => {
    if (query.length > 0) {
      analyticsHandlers.handleClickSearchSubmit(query, centre);
      searchOnSubmit(query);
    }
  }, [analyticsHandlers, centre, searchOnSubmit]);
  return /*#__PURE__*/_jsxs(CentreContext.Provider, {
    value: centre,
    children: [/*#__PURE__*/_jsxs(Head, {
      "data-id": "layout_index_Layout_Head",
      children: [title && /*#__PURE__*/_jsx("title", {
        "data-id": "layout_index_Layout_title",
        children: title
      }), description && /*#__PURE__*/_jsx("meta", {
        name: "description",
        content: description,
        "data-id": "layout_index_Layout_meta"
      }, "description"), twitterCreator && /*#__PURE__*/_jsx("meta", {
        name: "twitter:creator",
        content: twitterCreator,
        "data-id": "layout_index_Layout_meta_3"
      }), twitterCard && /*#__PURE__*/_jsx("meta", {
        name: "twitter:card",
        content: twitterCard,
        "data-id": "layout_index_Layout_meta_4"
      }), openGraphUrl && /*#__PURE__*/_jsx("meta", {
        property: "og:url",
        content: openGraphUrl,
        "data-id": "layout_index_Layout_meta_5"
      }), openGraphTitle && /*#__PURE__*/_jsx("meta", {
        property: "og:title",
        content: openGraphTitle,
        "data-id": "layout_index_Layout_meta_6"
      }), openGraphDescription && /*#__PURE__*/_jsx("meta", {
        property: "og:description",
        content: openGraphDescription,
        "data-id": "layout_index_Layout_meta_7"
      }), openGraphImage && /*#__PURE__*/_jsx("meta", {
        property: "og:image",
        content: openGraphImage,
        "data-id": "layout_index_Layout_meta_8"
      }), completeCanonical && /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx("meta", {
          property: "og:url",
          content: completeCanonical,
          "data-id": "layout_index_Layout_meta_9"
        }, "og:url"), /*#__PURE__*/_jsx("link", {
          rel: "canonical",
          href: completeCanonical,
          "data-id": "layout_index_Layout_link"
        }, "canonical")]
      }), showRobotsMetaTag && /*#__PURE__*/_jsx("meta", {
        name: "robots",
        content: "max-image-preview:large",
        "data-id": "layout_index_Layout_meta"
      })]
    }), /*#__PURE__*/_jsxs(AdTargetingContext.Provider, {
      value: {
        centreSlug: centre === null || centre === void 0 ? void 0 : centre.slug
      },
      children: [/*#__PURE__*/_jsxs("nav", {
        "aria-label": "Header",
        "data-id": "layout_index_Layout_nav",
        children: [showChrome && /*#__PURE__*/_jsxs(Header, {
          country: country,
          logoLink: logoLink,
          logoLabel: logoLabel,
          logoSlug: logoSlug,
          topStripProps: (_ref2 = showCVPStrip ? topStripProps : null) !== null && _ref2 !== void 0 ? _ref2 : undefined,
          user: user,
          taskMode: taskMode,
          backLink: backLink,
          onClickPrimaryMenu: () => setIsOpenPrimaryMenu(true),
          onClickSecondaryMenu: () => setIsOpenSecondaryMenu(true),
          onClickTopStrip: handleClickTopStrip,
          onClickLogin: () => registerRedirect(),
          onClickSearch: () => setIsOpenSearchModal(true),
          onClickBack: onClickBack,
          onClickLogo: analyticsHandlers.handleClickLogo,
          "data-id": "layout_index_Layout_Header",
          children: [/*#__PURE__*/_jsxs(HeaderMenus, {
            "data-id": "layout_index_Layout_Header_HeaderMenus",
            children: [/*#__PURE__*/_jsx(HeaderPrimaryMenu, {
              isOpen: isOpenPrimaryMenu,
              onClose: handleClosePrimaryMenu,
              country: country,
              centres: centres,
              centre: centre,
              giftCards: giftCards !== null && giftCards !== void 0 ? giftCards : [],
              onClickLink: analyticsHandlers.handleClickMenuLink,
              onClickButton: analyticsHandlers.handleClickMenuButton,
              "data-id": "layout_index_Layout_HeaderMenus_HeaderPrimaryMenu"
            }), /*#__PURE__*/_jsx(HeaderSecondaryMenu, {
              name: (_user$firstName = user === null || user === void 0 ? void 0 : user.firstName) !== null && _user$firstName !== void 0 ? _user$firstName : undefined,
              isOpen: isOpenSecondaryMenu,
              onClose: () => setIsOpenSecondaryMenu(false),
              onClickLink: analyticsHandlers.handleClickMenuLink,
              "data-id": "layout_index_Layout_HeaderMenus_HeaderSecondaryMenu"
            }), /*#__PURE__*/_jsx(HeaderSearch, {
              isOpen: isOpenSearchModal,
              onClose: () => setIsOpenSearchModal(false),
              centres: centres !== null && centres !== void 0 ? centres : [],
              getSources: getSources,
              specialSearchKind: specialSearchKind,
              country: country,
              onSubmit: onSubmit,
              reshape: reshape,
              "data-id": "layout_index_Layout_HeaderMenus_HeaderSearch"
            })]
          }), /*#__PURE__*/_jsxs(HeaderInner, {
            "data-id": "layout_index_Layout_Header_HeaderInner",
            children: [/*#__PURE__*/_jsx(HeaderNavigationBar, {
              primaryMenuItems: primaryMenuItems !== null && primaryMenuItems !== void 0 && primaryMenuItems.length ? primaryMenuItems : undefined,
              secondaryMenuItems: secondaryMenuItems,
              onClickLink: analyticsHandlers.handleClickNavigationBarLink,
              "data-id": "layout_index_Layout_HeaderInner_HeaderNavigationBar"
            }), /*#__PURE__*/_jsx(HeaderQuickLinks, {
              items: quickLinks,
              "data-id": "layout_index_Layout_HeaderInner_HeaderQuickLinks"
            })]
          }), /*#__PURE__*/_jsx(HeaderOuter, {
            "data-id": "layout_index_Layout_Header_HeaderOuter",
            children: /*#__PURE__*/_jsx(HeaderBreadcrumb, {
              items: breadcrumbs,
              "data-id": "layout_index_Layout_HeaderOuter_HeaderBreadcrumb"
            })
          })]
        }), pageTitle && /*#__PURE__*/_jsx(CoreContainer, {
          "data-id": "layout_index_Layout_CoreContainer",
          children: /*#__PURE__*/_jsx(H1, {
            sx: {
              marginBottom: 'spacing-sm'
            },
            "data-id": "layout_index_Layout_CoreContainer_H1",
            children: pageTitle
          })
        })]
      }), /*#__PURE__*/_jsx(Box, {
        as: "main",
        id: "main-content",
        style: mainStyles,
        sx: {
          flex: '1 0 auto'
        },
        "data-id": "layout_index_Layout_Box",
        children: children
      }), showChrome && showFooter && /*#__PURE__*/_jsx(Box, {
        sx: {
          marginTop: ['spacing-4xl', null, 'spacing-5xl']
        },
        "data-id": "layout_index_Layout_Box_26",
        children: taskMode ? /*#__PURE__*/_jsx(UniversalFooter, {
          country: country,
          taskMode: true,
          "data-id": "layout_index_Layout_UniversalFooter"
        }) : /*#__PURE__*/_jsx(UniversalFooter, {
          Link: Link,
          centre: centre,
          country: country,
          onFindACentre: () => {
            setMagicLink(MagicLinkValue.centres);
          },
          "data-id": "layout_index_Layout_UniversalFooter_28"
        })
      })]
    })]
  });
}